// ODS-EWO-033 P0. One declaration per Studio application, so the shell can ask
// what an application is and what it contains instead of holding eleven
// hardcoded arrays. Declarative only: the catalogue still owns lifecycle and
// availability, entitlements still come from the platform, and every route is
// still admitted by its own guard. This file adds no authority.

export type NavEntry = {
  readonly to: string;
  readonly label: string;
  readonly icon: string;
  /** Match this route exactly, for a parent that also has children. */
  readonly end?: boolean;
};

export type SubjectRoute = {
  /** The path the platform's resolver names, relative to `routePrefix`. */
  readonly route: string;
  /** The `intelligence_object_types.key` that path addresses. */
  readonly subjectType: string;
};

export type ApplicationManifest = {
  /** Stable identity, and the last segment of the route prefix. */
  readonly id: string;
  readonly name: string;
  readonly icon: string;
  /** The catalogue application this is: `catalog_applications.slug`. */
  readonly catalogSlug: string;
  /** Every route this application owns lives under here. */
  readonly routePrefix: string;
  /** Where the switcher lands. */
  readonly primaryRoute: string;
  /**
   * Every key an organization must hold to reach the application at all. More
   * than one where a surface is sold as an addition to its application, and the
   * same pair the route guard checks.
   */
  readonly entitlementKeys: readonly string[];
  readonly nav: readonly NavEntry[];
  /**
   * The application key the platform knows this by: the `owning_application` of
   * its Intelligence object types, and the scheme of their resolvers. Derived
   * from `id` unless a surface shares its application's key.
   */
  readonly platformApplication?: string;
  /**
   * The addressable objects this surface can actually render, as the platform's
   * registry writes them: the resolver path, and the registered Intelligence
   * object type that path addresses. One declaration serves both directions —
   * search lands on the object (P2), and the shell knows what the customer is
   * looking at so it can ask the Context Bridge about it (P4). A resolver this
   * router cannot serve is worse than no destination, so anything not declared
   * here falls back to `primaryRoute`.
   */
  readonly subjects?: readonly SubjectRoute[];
  /**
   * The application that owns this manifest, when the manifest is one of its
   * surfaces rather than an application in its own right. A surface is not an
   * entry in the switcher; Governance & Compliance is Policy AI's, not a ninth
   * application.
   */
  readonly surfaceOf?: string;
};

const CONTRACT_AI: ApplicationManifest = {
  id: "contract-ai",
  name: "Contract AI",
  icon: "📄",
  catalogSlug: "app-contract",
  routePrefix: "/studio/contract-ai",
  primaryRoute: "/studio/contract-ai",
  entitlementKeys: ["contract_ai.enabled"],
  nav: [
    { to: "/studio/contract-ai", label: "Dashboard", icon: "📄", end: true },
    { to: "/studio/contract-ai/repository", label: "Repository", icon: "🗂️" },
    { to: "/studio/contract-ai/review", label: "AI Review", icon: "✦" },
    { to: "/studio/contract-ai/ask", label: "Ask Your Contracts", icon: "💬" },
    { to: "/studio/contract-ai/clauses", label: "Clause Intelligence", icon: "⚖️" },
    { to: "/studio/contract-ai/obligations", label: "Obligations", icon: "✅" },
    { to: "/studio/contract-ai/compliance", label: "Compliance", icon: "🛡️" },
  ],
};

const PROPOSAL_AI: ApplicationManifest = {
  id: "proposal-ai",
  name: "Proposal AI",
  icon: "📝",
  catalogSlug: "app-proposal",
  routePrefix: "/studio/proposal-ai",
  primaryRoute: "/studio/proposal-ai",
  entitlementKeys: ["proposal_ai.enabled"],
  nav: [
    { to: "/studio/proposal-ai", label: "Dashboard", icon: "📝", end: true },
    { to: "/studio/proposal-ai/opportunities", label: "Opportunities", icon: "🎯" },
    { to: "/studio/proposal-ai/ask", label: "Ask the Solicitation", icon: "💬" },
    { to: "/studio/proposal-ai/compliance", label: "Compliance Center", icon: "🛡️" },
    { to: "/studio/proposal-ai/workspace", label: "Workspace", icon: "📝" },
    { to: "/studio/proposal-ai/knowledge", label: "Knowledge Library", icon: "📚" },
    { to: "/studio/proposal-ai/reviews", label: "Review Center", icon: "✦" },
    { to: "/studio/proposal-ai/submission", label: "Submission Center", icon: "📦" },
    { to: "/studio/proposal-ai/connected", label: "Connected Intelligence", icon: "🔗" },
    { to: "/studio/proposal-ai/analytics", label: "Analytics", icon: "📊" },
    { to: "/studio/proposal-ai/settings", label: "Settings", icon: "⚙️" },
  ],
};

const POLICY_AI: ApplicationManifest = {
  id: "policy-ai",
  name: "Policy AI",
  icon: "📕",
  catalogSlug: "app-policy",
  routePrefix: "/studio/policy-ai",
  primaryRoute: "/studio/policy-ai",
  entitlementKeys: ["policy_ai.enabled"],
  nav: [
    { to: "/studio/policy-ai", label: "Lifecycle Dashboard", icon: "📊", end: true },
    { to: "/studio/policy-ai/drafts", label: "Authoring", icon: "✍️" },
    { to: "/studio/policy-ai/acknowledgments", label: "Acknowledgments", icon: "✅" },
    { to: "/studio/policy-ai/exceptions", label: "Exceptions & Waivers", icon: "⚠️" },
  ],
};

const VENDOR_AI: ApplicationManifest = {
  id: "vendor-ai",
  name: "Vendor AI",
  icon: "🏭",
  catalogSlug: "app-vendor",
  routePrefix: "/studio/vendor-ai",
  primaryRoute: "/studio/vendor-ai",
  entitlementKeys: ["vendor_ai.enabled"],
  subjects: [
    { route: "/vendors/{id}", subjectType: "vendor" },
    { route: "/assessments/{id}", subjectType: "vendor_assessment" },
  ],
  nav: [
    { to: "/studio/vendor-ai", label: "Portfolio", icon: "🏭", end: true },
    { to: "/studio/vendor-ai/vendors", label: "Vendor register", icon: "📇" },
    { to: "/studio/vendor-ai/intake", label: "Intake queue", icon: "🗳️" },
    { to: "/studio/vendor-ai/assessments", label: "Assessments", icon: "📋" },
    { to: "/studio/vendor-ai/findings", label: "Findings", icon: "⚠️" },
  ],
};

const MEETING_AI: ApplicationManifest = {
  id: "meeting-ai",
  name: "Meeting AI",
  icon: "🗣️",
  catalogSlug: "app-meeting",
  routePrefix: "/studio/meeting-ai",
  primaryRoute: "/studio/meeting-ai",
  entitlementKeys: ["meeting_ai.enabled"],
  subjects: [{ route: "/meetings/{id}", subjectType: "meeting" }],
  nav: [
    { to: "/studio/meeting-ai", label: "Meeting AI", icon: "🗣️", end: true },
    { to: "/studio/meeting-ai/commitments", label: "Commitments", icon: "🤝" },
    { to: "/studio/meeting-ai/work-items", label: "Work items", icon: "📌" },
  ],
};

const REVIEW_AI: ApplicationManifest = {
  id: "review-ai",
  name: "Review AI",
  icon: "🔎",
  catalogSlug: "app-review",
  routePrefix: "/studio/review-ai",
  primaryRoute: "/studio/review-ai",
  entitlementKeys: ["review_ai.enabled"],
  subjects: [{ route: "/reviews/{id}", subjectType: "review" }],
  nav: [{ to: "/studio/review-ai", label: "Reviews", icon: "🔎", end: true }],
};

const CALENDAR_AI: ApplicationManifest = {
  id: "calendar-ai",
  name: "Calendar AI",
  icon: "📅",
  catalogSlug: "app-calendar",
  routePrefix: "/studio/calendar-ai",
  primaryRoute: "/studio/calendar-ai",
  entitlementKeys: ["calendar_ai.enabled"],
  nav: [
    { to: "/studio/calendar-ai", label: "Date authority", icon: "📅", end: true },
    { to: "/studio/calendar-ai/obligation-schedules", label: "Obligation schedules", icon: "🗓️" },
    { to: "/studio/calendar-ai/obligated-dates", label: "Obligated dates", icon: "⏰" },
    { to: "/studio/calendar-ai/source-bindings", label: "Source bindings", icon: "🔗" },
    { to: "/studio/calendar-ai/reflected-dates", label: "Reflected dates", icon: "🪞" },
  ],
};

const SOP_AI: ApplicationManifest = {
  id: "sop-ai",
  name: "SOP AI",
  icon: "✅",
  catalogSlug: "app-sop",
  routePrefix: "/studio/sop-ai",
  primaryRoute: "/studio/sop-ai",
  entitlementKeys: ["sop_ai.enabled"],
  subjects: [
    { route: "/procedures/{id}", subjectType: "procedure" },
    { route: "/runs/{id}", subjectType: "procedure_run" },
  ],
  nav: [{ to: "/studio/sop-ai", label: "Run dashboard", icon: "✅", end: true }],
};

// Policy AI's governance surface, sold as an addition to Policy AI and
// therefore requiring both keys. Declared as a surface so the switcher does not
// grow a ninth application for it.
const GOVERNANCE: ApplicationManifest = {
  id: "governance",
  name: "Governance & Compliance",
  icon: "🛡️",
  catalogSlug: "app-policy",
  routePrefix: "/studio/governance",
  primaryRoute: "/studio/governance",
  entitlementKeys: ["policy_ai.enabled", "policy_ai.governance_compliance.enabled"],
  platformApplication: "policy_ai",
  surfaceOf: "policy-ai",
  nav: [
    { to: "/studio/governance", label: "Overview", icon: "🛡️", end: true },
    { to: "/studio/governance/frameworks", label: "Frameworks", icon: "📚" },
    { to: "/studio/governance/mappings", label: "Mappings", icon: "🔗" },
    { to: "/studio/governance/evidence", label: "Evidence", icon: "🧾", end: true },
    { to: "/studio/governance/evidence/requests", label: "Evidence requests", icon: "📥" },
    { to: "/studio/governance/evidence/packages", label: "Evidence packages", icon: "📦" },
    { to: "/studio/governance/exceptions", label: "Exceptions", icon: "⚖️" },
    { to: "/studio/governance/regulations", label: "Regulations", icon: "📜" },
    { to: "/studio/governance/impact", label: "Impact", icon: "🧭" },
    { to: "/studio/governance/remediation", label: "Remediation", icon: "🛠️" },
    { to: "/studio/governance/audits", label: "Audits", icon: "🔍" },
    { to: "/studio/governance/reports", label: "Reports", icon: "📊" },
  ],
};

/** Every application and application surface the shell can frame. */
export const APPLICATION_MANIFESTS: readonly ApplicationManifest[] = [
  CONTRACT_AI,
  PROPOSAL_AI,
  POLICY_AI,
  VENDOR_AI,
  MEETING_AI,
  REVIEW_AI,
  CALENDAR_AI,
  SOP_AI,
  GOVERNANCE,
];

/** Studio's own navigation. Belongs to no application. */
export const GLOBAL_NAV: readonly NavEntry[] = [
  { to: "/studio", label: "Home", icon: "◇", end: true },
  { to: "/studio/attention", label: "Attention Center", icon: "⚡" },
  { to: "/studio/my-apps", label: "My Applications", icon: "🧩" },
  { to: "/studio/trials", label: "Trials", icon: "⏳" },
  { to: "/studio/documents", label: "Documents", icon: "🗂️" },
];

/** Organization administration. Also belongs to no application. */
export const ORGANIZATION_NAV: readonly NavEntry[] = [
  { to: "/studio/billing", label: "Billing", icon: "💳" },
  { to: "/studio/usage", label: "Usage", icon: "📊" },
  { to: "/studio/seats", label: "Seats", icon: "👥" },
  { to: "/studio/organization", label: "Organization", icon: "🏢" },
  { to: "/studio/users", label: "Users", icon: "🧑" },
  { to: "/studio/notifications", label: "Notifications", icon: "🔔" },
  { to: "/studio/settings", label: "Settings", icon: "⚙️" },
  { to: "/studio/support", label: "Support", icon: "🛟" },
];

const ACTIVE_STATUSES = ["ALLOWED", "TRIAL"];

/**
 * True when the organization holds every key the manifest requires. Reads the
 * entitlements the platform returned; it decides nothing itself, and the route
 * guards and the Worker check the same keys again.
 */
export function isEntitled(
  manifest: ApplicationManifest,
  entitlements: readonly { key: string; status: string }[] | null | undefined,
): boolean {
  if (!entitlements) return false;
  return manifest.entitlementKeys.every((key) =>
    entitlements.some((e) => e.key === key && ACTIVE_STATUSES.includes(e.status)),
  );
}

/** The key the platform knows an application by. */
export function platformApplicationOf(manifest: ApplicationManifest): string {
  return manifest.platformApplication ?? manifest.id.replace(/-/g, "_");
}

export type ResolvedDestination = {
  readonly manifest: ApplicationManifest;
  readonly route: string;
  /** False when the resolver named something this router cannot render, and the
   * destination is the application's own landing route instead. */
  readonly exact: boolean;
};

/** The identifier a template addresses in a path, when the path is that shape. */
function idIn(template: string, path: string): string | undefined {
  const parts = template.split("{id}");
  const before = parts[0] ?? "";
  const after = parts[1];
  if (parts.length !== 2 || after === undefined) return undefined;
  if (!path.startsWith(before) || !path.endsWith(after)) return undefined;
  if (path.length < before.length + after.length + 1) return undefined;
  const id = path.slice(before.length, path.length - after.length);
  return id.length > 0 && !id.includes("/") ? id : undefined;
}

/**
 * Where a search result's resolver lands in Studio. The platform states which
 * application owns an object and how to address it; this translates that into a
 * route without inventing one, and says whether it is the object itself or only
 * the application that holds it.
 */
export function destinationForResolver(
  resolver: string,
): ResolvedDestination | undefined {
  const match = /^([a-z][a-z0-9_]*):(\/.*)$/.exec(resolver);
  if (!match) return undefined;
  const application = match[1] ?? "";
  const path = match[2] ?? "";
  const candidates = APPLICATION_MANIFESTS.filter(
    (m) => platformApplicationOf(m) === application,
  );
  for (const manifest of candidates) {
    if (
      (manifest.subjects ?? []).some(
        (subject) => idIn(subject.route, path) !== undefined,
      )
    ) {
      return { manifest, route: `${manifest.routePrefix}${path}`, exact: true };
    }
  }
  const owner = candidates.find((m) => !m.surfaceOf) ?? candidates[0];
  return owner
    ? { manifest: owner, route: owner.primaryRoute, exact: false }
    : undefined;
}

export type SubjectInView = {
  readonly manifest: ApplicationManifest;
  /** The registered object type, as the Context Bridge expects it. */
  readonly subjectType: string;
  readonly subjectId: string;
};

/**
 * The business object the customer is looking at, when the route addresses one
 * the application declared. Declaration-driven on purpose: the shell cannot
 * guess that a path segment is an object identifier, and asking the Context
 * Bridge about something no application claims would produce a refusal rather
 * than context (ODS-EWO-033 P4).
 */
export function subjectForPath(pathname: string): SubjectInView | undefined {
  const manifest = manifestForPath(pathname);
  if (!manifest) return undefined;
  const path = pathname.slice(manifest.routePrefix.length);
  for (const subject of manifest.subjects ?? []) {
    const subjectId = idIn(subject.route, path);
    if (subjectId !== undefined) {
      return { manifest, subjectType: subject.subjectType, subjectId };
    }
  }
  return undefined;
}

/** The application a Studio path belongs to, if any. */
export function manifestForPath(pathname: string): ApplicationManifest | undefined {
  return APPLICATION_MANIFESTS.find(
    (m) => pathname === m.routePrefix || pathname.startsWith(`${m.routePrefix}/`),
  );
}
